import { GetStatisticResponseChartsDto } from '@EHSNavigator/backend';

const defaultColors = ['#B6244F', '#ed7a3a', '#ffae00', '#168553'];

const generateRandomOpacityColor = (baseColor: string) => {
  const r = parseInt(baseColor?.slice(1, 3), 16);
  const g = parseInt(baseColor?.slice(3, 5), 16);
  const b = parseInt(baseColor?.slice(5, 7), 16);
  const alpha = (0.5 + Math?.random() * 0.5)?.toFixed(2);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildColorArray = (chartData: { name: string }[]) => {
  const extraCount = chartData?.length - defaultColors?.length;
  const resultColors = defaultColors?.slice(
    0,
    Math?.min(defaultColors?.length, chartData?.length),
  );
  if (extraCount > 0) {
    for (let i = 0; i < extraCount; i++) {
      const baseColor = defaultColors?.[i % defaultColors?.length];
      resultColors?.push(generateRandomOpacityColor(baseColor));
    }
  }
  return resultColors;
};

export const totalIncidentsReportedOptions = (
  series: GetStatisticResponseChartsDto['detailsInsightsTrends']['totalIncidentReportedChart'],
) => {
  const options = {
    chart: {
      height: 200,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        barHeight: '90%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        endingShape: 'rounded',
        distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      colors: ['transparent', 'transparent', '#F6CD13'],
      width: [2, 2, 2],
    },
    xaxis: {
      categories:
        series?.length > 0
          ? series?.map((node) => node?.month)
          : [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        rotate: 0,
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value.toFixed(0);
        },
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#3957df', '#168553', '#F6CD13'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      markers: {
        size: 7,
        shape: ['square', 'square', 'line'],
        strokeWidth: [0, 0, 2],
      },
    },
    grid: {
      show: true,
      borderColor: '#DDE3E7',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const baseSeries = [
    {
      name: 'Incidents Reported',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.reported)
          : [7, 20, 14, 10, 13, 4, 9, 12, 14, 10, 8, 6],
    },
    {
      name: 'Resolved',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.resolved)
          : [6, 6, 6, 6, 6, 4, 7, 10, 12, 8, 6, 5],
    },
    {
      name: 'Trend',
      type: 'line',
      data:
        series?.length > 0
          ? series?.map((node) => node?.trend)
          : [6, 20, 14, 10, 13, 4, 8, 11, 13, 9, 7, 5.5],
    },
  ];
  return {
    options,
    series: baseSeries,
  };
};

export const totalIncidentsReportedByDepartmentOptions = (
  series: GetStatisticResponseChartsDto['detailsInsightsTrends']['totalIncidentReportedByDepartmentChart'],
) => {
  const options = {
    chart: {
      height: 200,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      colors: ['transparent', 'transparent'],
      width: [2, 2],
    },
    xaxis: {
      categories:
        series?.length > 0
          ? series?.map((node) => node?.department)
          : [
              'Packaging',
              'Material Handling',
              'Electricity',
              'Logistics',
              'Main Factory',
            ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        hideOverlappingLabels: false,
        trim: true,
        rotate: 0,
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value.toFixed(0);
        },
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#3957df', '#168553'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      markers: {
        size: 7,
        shape: ['square', 'square'],
        strokeWidth: [0, 0],
      },
    },
    grid: {
      show: true,
      borderColor: '#DDE3E7',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const baseSeries = [
    {
      name: 'Incidents Reported',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.reported)
          : [7, 20, 14, 10, 13],
    },
    {
      name: 'Resolved',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.resolved)
          : [6, 6, 6, 6, 6],
    },
  ];
  return {
    options,
    series: baseSeries,
  };
};

export const incidentsByTypeOptions = (
  series: GetStatisticResponseChartsDto['detailsInsightsTrends']['incidentTypeChart'],
) => {
  const options = {
    chart: {
      height: 200,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        barHeight: '90%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        endingShape: 'rounded',
        distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      colors: ['transparent', '#3957DF'],
      width: [2, 2],
    },
    xaxis: {
      categories:
        series?.length > 0
          ? series?.map((node) => node?.type)
          : [
              'Near Miss',
              'Injury/Illness',
              'Environment Hazard',
              'Vehicle',
              'Property Damage',
              'Other',
            ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        hideOverlappingLabels: false,
        trim: true,
        rotate: 0,
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value.toFixed(0);
        },
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#ED7A3A', '#3957DF'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      markers: {
        size: 7,
        shape: ['square', 'line'],
        strokeWidth: [0, 2],
      },
    },
    grid: {
      show: true,
      borderColor: '#DDE3E7',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const baseSeries = [
    {
      name: 'Incidents Reported',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.reported)
          : [20, 11, 12, 20, 20, 20],
    },

    {
      name: 'Trend',
      type: 'line',
      data:
        series?.length > 0
          ? series?.map((node) => node?.trend)
          : [20, 11, 12, 20, 20, 0],
    },
  ];
  return {
    options,
    series: baseSeries,
  };
};

export const correctiveActionsOverviewOptions = (
  chartData: GetStatisticResponseChartsDto['detailsInsightsTrends']['correctiveActionOverviewChart'] = [],
) => {
  const options = {
    stroke: {
      show: false,
      curve: 'straight',
      lineCap: 'butt',
      colors: '#ffffff',
      width: 5,
      dashArray: 0,
    },
    chart: {
      type: 'donut',
      height: 200,
      animations: {
        enabled: true,
      },
    },
    tooltip: {
      enabled: false,
    },
    labels:
      chartData?.length > 0
        ? chartData?.map((node) => node?.name)
        : ['Near Miss', 'Injury/Illness', 'Environment Hazard'],
    colors: chartData?.every((value) => !value?.total)
      ? defaultColors
      : buildColorArray(chartData),
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'right',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      width: 250,
      markers: {
        size: 7,
        shape: 'square',
        strokeWidth: [0, 0, 0],
      },
      formatter: function (val: any, opts: any) {
        return (
          val +
          '<span>' +
          opts.w.globals.series[opts.seriesIndex] +
          '%</span>' +
          '<span>' +
          (chartData?.length > 0
            ? chartData?.[opts.seriesIndex]?.total
            : [24, 54, 42]?.[opts.seriesIndex]) +
          '</span>'
        );
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: 36,
          labels: {
            show: false,
          },
        },
      },
    },
  };

  function calculatePercentages(totals: number[]) {
    const allZero = totals?.every((value) => !value);
    if (allZero) {
      return [24, 54, 42];
    }
    const sum = totals?.reduce((acc, val) => acc + val, 0);
    return totals?.map((value) => Number(((value / sum) * 100)?.toFixed(2)));
  }
  const response = calculatePercentages(
    chartData?.length > 0 ? chartData?.map((node) => node?.total) : [0, 0, 0],
  );

  const series = response;
  return {
    options,
    series,
  };
};

export const incidentsBySeverityOptions = (
  chartData: GetStatisticResponseChartsDto['detailsInsightsTrends']['incidentSeverityChart'] = [],
) => {
  const severityColorMap = {
    Critical: '#B6244F',
    Major: '#ed7a3a',
    Moderate: '#ffae00',
    Low: '#168553',
  };

  const options = {
    stroke: {
      show: false,
      curve: 'straight',
      lineCap: 'butt',
      colors: '#ffffff',
      width: 5,
      dashArray: 0,
    },
    chart: {
      type: 'donut',
      height: 200,
      animations: {
        enabled: true,
      },
    },
    tooltip: {
      enabled: false,
    },
    labels:
      chartData?.length > 0
        ? chartData?.every((value) => !value?.total)
          ? ['Critical', 'Major', 'Moderate', 'Low']
          : chartData?.map((node) => node?.severity)
        : ['Critical', 'Major', 'Moderate', 'Low'],
    colors: chartData?.map(
      (node) =>
        severityColorMap[node?.severity as keyof typeof severityColorMap],
    ),
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'right',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      width: 250,
      markers: {
        size: 7,
        shape: ['square', 'square', 'square', 'square'],
        strokeWidth: [0, 0, 0],
      },
      formatter: function (val: any, opts: any) {
        return (
          val +
          '<span>' +
          opts.w.globals.series[opts.seriesIndex] +
          '%</span>' +
          '<span>' +
          chartData[opts.seriesIndex]?.total +
          '</span>'
        );
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: 36,
          labels: {
            show: false,
          },
        },
      },
    },
  };

  function calculatePercentages(totals: number[]) {
    const allZero = totals?.every((value) => !value);
    if (allZero) {
      return [24, 54, 42, 32];
    }
    const sum = totals.reduce((acc, val) => acc + val, 0);
    return totals.map((value) => Number(((value / sum) * 100).toFixed(2)));
  }

  const response = calculatePercentages(
    chartData?.map((node) => node?.total) || [],
  );
  const series = response;
  return {
    options,
    series,
  };
};

export const correctiveActionByDepartmentOptions = (
  series: GetStatisticResponseChartsDto['detailsInsightsTrends']['correctiveActionOverviewChart'] = [],
) => {
  const options = {
    chart: {
      height: 200,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        barHeight: '90%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        endingShape: 'rounded',
        distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      colors: ['transparent', '#3957DF'],
      width: [2, 2],
    },
    xaxis: {
      categories:
        series?.length > 0
          ? series?.map((node) => node?.name)
          : [
              'Near Miss',
              'Injury/Illness',
              'Environment Hazard',
              'Vehicle',
              'Property Damage',
              'Other',
            ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        hideOverlappingLabels: false,
        trim: true,
        rotate: 0,
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value.toFixed(0);
        },
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#ED7A3A', '#3957DF'],
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'bottom',
      horizontalAlign: 'left',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        whiteSpace: 'nowrap',
      },
      markers: {
        size: 7,
        shape: ['square', 'line'],
        strokeWidth: [0, 2],
      },
    },
    grid: {
      show: true,
      borderColor: '#DDE3E7',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const baseSeries = [
    {
      name: 'Corrective Actions',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.total)
          : [20, 11, 12, 20, 20, 20],
    },
  ];
  return {
    options,
    series: baseSeries,
  };
};

export const typeOfLossOptions = (
  series: GetStatisticResponseChartsDto['detailsInsightsTrends']['typeofLossChart'],
) => {
  const options = {
    chart: {
      height: 200,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        barHeight: '90%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        endingShape: 'rounded',
        distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      colors: ['transparent', '#F6CD13'],
      width: [2, 2, 2],
    },
    xaxis: {
      categories:
        series?.length > 0
          ? series?.map((node) => node?.label)
          : [
              'Death',
              'Loss of consciousness',
              'Days away from work',
              'Restricted work activity or job transfer',
              'Medical treatment beyond first aid',
            ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        hideOverlappingLabels: false,
        trim: true,
        rotate: 0,
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value.toFixed(0);
        },
        style: {
          colors: '#7f7f82',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#3957df', '#F6CD13'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      markers: {
        size: 7,
        shape: ['square', 'line'],
        strokeWidth: [0, 2],
      },
    },
    grid: {
      show: true,
      borderColor: '#DDE3E7',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const baseSeries = [
    {
      name: 'Total',
      type: 'column',
      data:
        series?.length > 0
          ? series?.map((node) => node?.total)
          : [7, 20, 14, 10, 13],
    },
    {
      name: 'Trend',
      type: 'line',
      data:
        series?.length > 0
          ? series?.map((node) => node?.trend)
          : [6, 20, 14, 10, 13],
    },
  ];
  return {
    options,
    series: baseSeries,
  };
};

export const affectedBodyPartOptions = (
  chartData: GetStatisticResponseChartsDto['detailsInsightsTrends']['affectedBodyPartChart'] = [],
) => {
  const options = {
    stroke: {
      show: false,
      curve: 'straight',
      lineCap: 'butt',
      colors: '#ffffff',
      width: 5,
      dashArray: 0,
    },
    chart: {
      type: 'donut',
      height: 200,
      animations: {
        enabled: true,
      },
    },
    tooltip: {
      enabled: false,
    },
    labels:
      chartData?.length > 0
        ? chartData?.map((node) => node?.part)
        : ['Head', 'Feet', 'Arms', 'Torso', 'Legs', 'Hands'],

    colors: chartData?.every((value) => !value?.total)
      ? defaultColors
      : buildColorArray(chartData?.map((node) => ({ name: node?.part }))),
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'right',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      width: 250,
      markers: {
        size: 7,
        shape: ['square', 'square', 'square', 'square'],
        strokeWidth: [0, 0, 0],
      },
      formatter: function (val: any, opts: any) {
        return (
          val +
          '<span>' +
          opts.w.globals.series[opts.seriesIndex] +
          '%</span>' +
          '<span>' +
          chartData[opts.seriesIndex]?.total +
          '</span>'
        );
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: 36,
          labels: {
            show: false,
          },
        },
      },
    },
  };

  function calculatePercentages(totals: number[]) {
    const allZero = totals?.every((value) => !value);
    if (allZero) {
      return [20, 20, 20, 20, 20, 20];
    }
    const sum = totals.reduce((acc, val) => acc + val, 0);
    return totals.map((value) => Number(((value / sum) * 100).toFixed(2)));
  }

  const response = calculatePercentages(
    chartData?.map((node) => node?.total) || [],
  );
  const series = response;
  return {
    options,
    series,
  };
};

export const classificationIllnessOptions = (
  chartData: GetStatisticResponseChartsDto['detailsInsightsTrends']['classificationIllnessChart'] = [],
) => {
  const options = {
    stroke: {
      show: false,
      curve: 'straight',
      lineCap: 'butt',
      colors: '#ffffff',
      width: 5,
      dashArray: 0,
    },
    chart: {
      type: 'donut',
      height: 200,
      animations: {
        enabled: true,
      },
    },
    tooltip: {
      enabled: false,
    },
    labels:
      chartData?.length > 0
        ? chartData?.map((node) => node?.classification)
        : ['Head', 'Torso', 'Legs', 'Hands'],

    colors: chartData?.every((value) => !value?.total)
      ? defaultColors
      : buildColorArray(
          chartData?.map((node) => ({ name: node?.classification })),
        ),
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'right',
      labels: {
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
      },
      width: 250,
      markers: {
        size: 7,
        shape: ['square', 'square', 'square', 'square'],
        strokeWidth: [0, 0, 0],
      },
      formatter: function (val: any, opts: any) {
        return (
          val +
          '<span>' +
          opts.w.globals.series[opts.seriesIndex] +
          '%</span>' +
          '<span>' +
          `${chartData[opts.seriesIndex]?.total || 0}` +
          '</span>'
        );
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: 36,
          labels: {
            show: false,
          },
        },
      },
    },
  };

  function calculatePercentages(totals: number[]) {
    const allZero = totals?.every((value) => !value);
    if (allZero) {
      // Return an array of 20s based on chartData?.length, default to 4 if length is 0 or undefined
      const length = chartData?.length || 0;
      return Array(length > 0 ? length : 4).fill(25);
    }
    const sum = totals.reduce((acc, val) => acc + val, 0);
    return totals.map((value) => Number(((value / sum) * 100).toFixed(2)));
  }

  const response = calculatePercentages(
    chartData?.map((node) => node?.total) || [],
  );

  const series = response;

  return {
    options,
    series,
  };
};
